import { TZDate } from "@date-fns/tz";
import * as Sentry from "@sentry/nextjs";
import { useMemo } from "react";
import {
  DayOfTheWeek,
  FulfillmentType,
  type Period,
} from "~/graphql-client/generated";
import type { OrderDispatchOption, Product, TimeSlot } from "~/types";
import { useLocation } from "./locations";
import { useMenuContext } from "./menu-context";

export function useMenu(
  fulfillmentType: OrderDispatchOption | null,
  timeSlot: TimeSlot | null,
) {
  return _useMenu(fulfillmentType, timeSlot, false);
}

export function useBaseMenu(
  fulfillmentType: OrderDispatchOption | null,
  timeSlot: TimeSlot | null,
) {
  return _useMenu(fulfillmentType, timeSlot, true);
}

function _useMenu(
  fulfillmentType: OrderDispatchOption | null,
  timeSlot: TimeSlot | null,
  baseMenuOnly: boolean,
) {
  const { baseOrdering, fullOrdering, loadingBase, loadingFull } =
    // biome-ignore lint/correctness/useHookAtTopLevel: _useMenu is a hook
    useMenuContext();

  // biome-ignore lint/correctness/useHookAtTopLevel: _useMenu is a hook
  const { location } = useLocation();

  // biome-ignore lint/correctness/useHookAtTopLevel: _useMenu is a hook
  return useMemo(() => {
    const ordering = baseMenuOnly ? baseOrdering : fullOrdering;

    const allMenus = ordering?.menus ?? [];

    let menus = allMenus;
    if (location) {
      menus = menus.filter(availableToTimeSlot(timeSlot, location.timezone));

      // If no menus are available and a time slot not selected, show the next
      // available menus
      if (!timeSlot && !menus.length) {
        menus = allMenus.reduce<[typeof allMenus, number]>(
          ([menus, bestTime], menu) => {
            const nextTime = nextAvailableTime(
              location.timezone,
              menu.serviceAvailability,
            );
            if (!nextTime || nextTime > bestTime) return [menus, bestTime];
            if (nextTime === bestTime) return [menus.concat(menu), bestTime];
            return [[menu], nextTime];
          },
          [[], Number.MAX_SAFE_INTEGER],
        )[0];
      }
    }

    const categories = menus.flatMap((m) => m.categories ?? []);

    const seen = new Set<string>();
    const menu = categories.filter((c) => {
      if (!c.products?.length) return false;
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });

    if (!fulfillmentType) {
      return {
        menu: [],
        isLoading: true,
        isValidating: true,
        serviceAvailability: null,
      };
    }

    return {
      menu,
      isLoading: baseMenuOnly ? loadingBase : loadingFull,
      serviceAvailability: ordering?.menus?.length
        ? ordering?.menus[0].serviceAvailability
        : null,
    };
  }, [
    baseMenuOnly,
    baseOrdering,
    fullOrdering,
    location,
    fulfillmentType,
    loadingBase,
    loadingFull,
    timeSlot,
  ]);
}

export function useProduct(
  dispatchOption: OrderDispatchOption | null,
  timeSlot: TimeSlot | null,
  productId?: string,
): { product: Product | null; loading: boolean } {
  const { isLoading, menu } = useMenu(dispatchOption, timeSlot);

  return useMemo(() => {
    if (!productId) {
      return {
        product: null,
        loading: isLoading,
      };
    }

    for (const category of menu) {
      const product = findProduct(category.products || [], productId);
      if (product) {
        return {
          product: product?.available ? product : null,
          loading: isLoading,
        };
      }
    }

    return {
      product: null,
      loading: isLoading,
    };
  }, [isLoading, menu, productId]);
}

function findProduct(products: Product[], productId: string): Product | null {
  for (const product of products) {
    if (product.id === productId) return product;

    for (const option of product.options || []) {
      const childProduct = findProduct(
        option.choices.flatMap((choice) => choice.product),
        productId,
      );

      if (childProduct) return childProduct;
    }
  }

  return null;
}

export function toEnumFulfillmentType(
  option: OrderDispatchOption | undefined,
): FulfillmentType | undefined {
  if (option === "PICK_UP") {
    return FulfillmentType.PickUp;
  }
  if (option === "DELIVERY") {
    return FulfillmentType.Delivery;
  }
  if (option === "SERVICE") {
    return FulfillmentType.Service;
  }
}

const DAYS = [
  DayOfTheWeek.Sunday,
  DayOfTheWeek.Monday,
  DayOfTheWeek.Tuesday,
  DayOfTheWeek.Wednesday,
  DayOfTheWeek.Thursday,
  DayOfTheWeek.Friday,
  DayOfTheWeek.Saturday,
];

function availableToTimeSlot(
  timeSlot: TimeSlot | null,
  locationTimezone: string,
) {
  try {
    const d = timeSlot
      ? new TZDate(timeSlot.startTime, locationTimezone)
      : TZDate.tz(locationTimezone);

    try {
      Sentry.setContext("availableToTimeSlot/d", {
        d: {
          timeZone: d.timeZone,
          internal: (d as any).internal,
        },
      });
    } catch {}
    try {
      Sentry.setContext("availableToTimeSlot/d/valueOf", {
        d: d.valueOf(),
      });
    } catch {}

    const day = DAYS[d.getDay()];
    const time = d.toTimeString().slice(0, 5);

    return (m: { serviceAvailability?: Period[] | null }): boolean =>
      m.serviceAvailability?.some((p) => {
        return p.dayOfTheWeek === day && p.start <= time && p.end > time;
      }) ?? false;
  } catch (err) {
    Sentry.setContext("Debug availableToTimeSlot", {
      locationTimezone: locationTimezone,
      timeSlotStartTime: timeSlot?.startTime,
      timeSlotRef: timeSlot?.ref,
      timeSlotAvailable: timeSlot?.available,
    });
    Sentry.captureException(err);
    throw err;
  } finally {
    Sentry.setContext("availableToTimeSlot/d", null);
    Sentry.setContext("availableToTimeSlot/d/valueOf", null);
  }
}

function nextAvailableTime(
  locationTimezone: string,
  serviceAvailability?: Period[] | null,
): number | null {
  if (!serviceAvailability) return null;
  const now = TZDate.tz(locationTimezone);

  return serviceAvailability?.reduce((best: number | null, period: Period) => {
    const start = new TZDate(now, now.timeZone); // Clone now, keeping the timezone

    const [hours, minutes] = period.start.split(":").map(Number);
    const day = DAYS.indexOf(period.dayOfTheWeek);

    start.setHours(hours, minutes, 0, 0);
    start.setDate(start.getDate() + ((day + 7 - start.getDay()) % 7));
    if (start < now) start.setDate(start.getDate() + 7);
    const startTime = start.getTime();

    if (!best) return startTime;
    if (startTime < best) return startTime;
    return best;
  }, null);
}
